import { type FC, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/components/podkit/lib/cn";
import { convertUrlsToLinks } from "@/utils/url-conversion";
import { MarkdownCopyablePre } from "@/components/ai/chat/MarkdownCopyablePre";
import { MermaidCodePreview } from "@/components/ai/chat/MermaidCodePreview";

// This implements the blinking cursor effect for text that is being streamed in.
//
// At a high level the technique is to use a :after pseudo element to style an inline-block after the last child of the last child.
// The block is then styled to look like a cursor and the blinking is achieved using a simple opacity-based animation. When we're done
// streaming, we fade out the cursor.
//
// Some notes on the animation:
//
// - We're using a delay of 300ms so that the cursor only starts blinking if there's a streaming pause of at least 300ms. Because ReactMarkdown
//   re-renders the node when we change the content, this means the delay is reset whenever the content changes.
//
// - When a block is completed, we're fading out the cursor. This is done by setting the data-completed attribute to true on the parent,
//   which we then use to selectively modify the background color from secondary (when it's not completed) to transparent (when it is).
//   We're using a data-attribute rather than a prop to this component because ReactMarkdown performs excessive re-renders which replaces DOM nodes
//   and breaks the cursor effect.
//
// All of this is only enabled if data-completed is set on the parent container.
//
const blinkingCursorClassName = [
    // This selector is a bit, much so here's a drill-down:
    //
    // [.markdown-container>&:last-child] selector targets
    // - The last child in the group
    // - the last child inside of *that* element
    // Which ensures that we only add the cursor (|) after the very last character of the entire message
    //
    // The [[data-completed=false]_.markdown-container>&:last-child] selector does the same,
    // but only when the data-completed attribute it set to false (or true) on the parent.
    //
    // We're using [data-completed] to apply styles regardless of the value of the data property.
    "[[data-completed]_.markdown-container>&:last-child]:after:transition-colors [[data-completed]_.markdown-container>&:last-child]:after:[transition-duration:300ms] [[data-completed]_.markdown-container>&:last-child]:after:content-[' '] [[data-completed]_.markdown-container>&:last-child]:after:inline-block [[data-completed]_.markdown-container>&:last-child]:after:h-[0.8lh] [[data-completed]_.markdown-container>&:last-child]:after:ml-[1px] [[data-completed]_.markdown-container>&:last-child]:after:w-[6px] [[data-completed]_.markdown-container>&:last-child]:after:rounded-sm [[data-completed]_.markdown-container>&:last-child]:after:align-text-top",
    // When it's done, we hide the cursor by setting the bg to transparent
    "[[data-completed=true]_.markdown-container>&:last-child]:after:bg-transparent",
    // when it's in-progress we blink the cursor after 300ms for inactivity
    "[[data-completed=false]_.markdown-container>&:last-child]:after:bg-content-secondary [[data-completed=false]_.markdown-container>&:last-child]:after:animate-blink [[data-completed=false]_.markdown-container>&:last-child]:after:delay-300",
].join(" ");

type MarkdownProps = {
    content: string;
    className?: string;
};

const MarkdownBase: FC<MarkdownProps> = ({ content, className = "", ...props }) => {
    if (!content) return null;

    const components: Components = {
        h1: ({ children, ...props }) => (
            <h1 className={cn("pb-2 text-xl font-medium", blinkingCursorClassName)} {...props}>
                {children}
            </h1>
        ),
        h2: ({ children, ...props }) => (
            <h2 className={cn("pb-1 text-lg font-medium", blinkingCursorClassName)} {...props}>
                {children}
            </h2>
        ),
        h3: ({ children, ...props }) => (
            <h3 className={cn("pb-2 text-md font-medium", blinkingCursorClassName)} {...props}>
                {children}
            </h3>
        ),
        ul: ({ children, ...props }) => (
            <ul className={cn("mb-4 list-disc pl-8", blinkingCursorClassName)} {...props}>
                {children}
            </ul>
        ),
        ol: ({ children, ...props }) => (
            <ol className={cn("mb-4 list-decimal pl-8 last:mb-0", blinkingCursorClassName)} {...props}>
                {children}
            </ol>
        ),
        li: ({ children, ...props }) => (
            <li className={cn("mb-1", blinkingCursorClassName)} {...props}>
                {children}
            </li>
        ),
        a: ({ children, ...props }) => (
            <a
                className={cn("text-content-link hover:underline", blinkingCursorClassName)}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
            >
                {children}
            </a>
        ),
        blockquote: ({ children, ...props }) => (
            <blockquote
                className={cn(
                    "mb-4 border-l-4 border-border-base py-1 pl-4 text-content-muted",
                    blinkingCursorClassName,
                )}
                {...props}
            >
                {children}
            </blockquote>
        ),
        table: ({ children, ...props }) => (
            <div className="mb-4 overflow-hidden overflow-x-auto rounded-lg border border-border-subtle [overflow-wrap:normal] [word-break:normal]">
                <table className="min-w-full" {...props}>
                    {children}
                </table>
            </div>
        ),
        thead: ({ children, ...props }) => (
            <thead className="border-b border-border-subtle bg-surface-muted" {...props}>
                {children}
            </thead>
        ),
        th: ({ children, ...props }) => (
            <th
                className={cn("whitespace-nowrap px-4 py-2 text-left text-sm font-semibold", blinkingCursorClassName)}
                {...props}
            >
                {children}
            </th>
        ),
        td: ({ children, ...props }) => (
            <td
                className={cn(
                    "max-w-xs break-words border-t border-border-subtle px-4 py-2 text-sm",
                    blinkingCursorClassName,
                )}
                {...props}
            >
                {children}
            </td>
        ),
        p: ({ node, children, ...props }) => {
            type MarkdownNode = {
                tagName?: string;
            };
            const childNodes = node?.children as MarkdownNode[];
            if (childNodes?.[0]?.tagName === "img" && childNodes.length === 1) {
                return <>{children}</>;
            }
            return (
                <p className={cn("mb-4 last:mb-0", blinkingCursorClassName)} {...props}>
                    {children}
                </p>
            );
        },
        code: ({ children, ...props }) => {
            return (
                <code
                    className={cn(
                        "rounded-sm bg-surface-muted px-0.5 font-mono text-content-muted",
                        blinkingCursorClassName,
                    )}
                    {...props}
                >
                    {children}
                </code>
            );
        },
        pre: ({ children, ...props }) => {
            // Check if this is a Mermaid code block
            const codeElement = children as React.ReactElement;
            const codeProps = codeElement?.props as { className?: string; children?: React.ReactNode } | undefined;
            const language = codeProps?.className?.replace("language-", "") || "";
            const codeContent = codeProps?.children;

            if (language === "mermaid" && typeof codeContent === "string") {
                return <MermaidCodePreview content={codeContent} className={blinkingCursorClassName} />;
            }

            return (
                <MarkdownCopyablePre
                    className={cn(
                        "mb-4 flex rounded-md border border-border-subtle bg-surface-muted px-2 pb-1 pt-2 font-mono text-sm last:mb-0 [.ona_&_code]:bg-transparent",
                        "[.user-message_&]:border-1 [.user-message_&]:rounded-lg [.user-message_&]:border-border-subtle [.user-message_&]:bg-surface-primary",
                        blinkingCursorClassName,
                    )}
                    {...props}
                >
                    {children}
                </MarkdownCopyablePre>
            );
        },
        img: ({ ...props }) => <img className="my-2 h-auto max-w-full rounded-md" {...props} alt={props.alt || ""} />,
        hr: () => <hr className="my-6 border-t border-border-strong" />,
        strong: ({ children, ...props }) => (
            <strong className={cn("font-semibold", blinkingCursorClassName)} {...props}>
                {children}
            </strong>
        ),
        em: ({ children, ...props }) => (
            <em className={cn("italic", blinkingCursorClassName)} {...props}>
                {children}
            </em>
        ),
    };

    // Convert plain URLs to markdown links before processing
    const processedContent = convertUrlsToLinks(content);

    return (
        <div
            className={cn(
                "markdown-container",
                /* Break at word boundaries */
                "[word-break:break-word]",
                /* Fallback: Break at any character. This acts a fallback as it only triggers if the text would otherwise overflow */
                "[overflow-wrap:anywhere]",
                className,
            )}
        >
            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]} {...props}>
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

/**
 * Memoized version of Markdown that only re-renders when the content changes.
 * This is needed as the ReactMarkdown library we're using doesn't handle content changes well -
 * - it would rerender even when provided with the exact same content.
 */
export const Markdown = memo(MarkdownBase, (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
});
